/**
 * What "Next" does on the address screen: validate the whole screen, confirm
 * a suspected residential address, then persist the location, contact,
 * reference and loading capabilities in one call.
 */

import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { bookShipmentAddressAndContactRequest } from "../Helpers/bookShipmentAddressAndContactRequest";
import type { useBookShipmentAddressLoadingCapabilities } from "./useBookShipmentAddressLoadingCapabilities";
import type { useBookShipmentAddressValidation } from "./useBookShipmentAddressValidation";
import type { useBookShipmentAmazonReference } from "./useBookShipmentAmazonReference";
import type { useBookShipmentResidentialCheck } from "./useBookShipmentResidentialCheck";

interface BookShipmentAddressSaveInput {
  shipmentId: string;
  context: LocationContext;
  location: PreQuoteLocation;
  contact: Contact;
  isContactNotRequired: boolean;
  referenceNumber: string;
  amazon: ReturnType<typeof useBookShipmentAmazonReference>;
  boothNumber: string;
  deadlineDate: string | undefined;
  markLocationAsFavourite: boolean | undefined;
  loadingCapabilities: ReturnType<
    typeof useBookShipmentAddressLoadingCapabilities
  >;
  validation: ReturnType<typeof useBookShipmentAddressValidation>;
  residentialCheck: ReturnType<typeof useBookShipmentResidentialCheck>;
  setForceValidation: (force: boolean) => void;
  onComplete: () => void;
}

export function useBookShipmentAddressSave(
  input: BookShipmentAddressSaveInput,
) {
  const shipmentsApi = useShipmentsApi();

  return async function nextIsValid(): Promise<boolean> {
    input.setForceValidation(true);

    if (!input.validation.isScreenCompletelyEntered()) {
      return false;
    }

    if (!(await input.residentialCheck.confirmNotResidential())) {
      return false;
    }

    const { amazon } = input;
    await shipmentsApi.setAddressAndContact(
      bookShipmentAddressAndContactRequest({
        shipmentId: input.shipmentId,
        context: input.context,
        location: input.location,
        contact: input.contact,
        isContactNotRequired: input.isContactNotRequired,
        referenceNumber: input.referenceNumber,
        amazonReferenceId: amazon.isAmazonDelivery
          ? amazon.amazonReferenceId
          : undefined,
        amazonShipmentId: amazon.isAmazonDelivery
          ? amazon.amazonShipmentId
          : undefined,
        boothNumber: input.boothNumber,
        deadline: input.deadlineDate,
        markLocationAsFavourite: input.markLocationAsFavourite,
        locationHasDock: input.loadingCapabilities.locationHasDock,
        locationHasForklift: input.loadingCapabilities.locationHasForklift,
      }),
    );
    input.onComplete();
    return true;
  };
}
